import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Input, Button, Alert, Badge, phoneLengthError } from '@bbpos/ui';
import type { CustomerLookupResult as UICustomerLookupResult } from '@bbpos/ui/src/components/POSBillingComponents/CustomerLookup/types';
import { customersService } from '@/services/customers.service';
import type {
  CustomerLookupResult as ApiCustomerLookupResult,
  CustomerListItem,
} from '@/services/customers.service';
import { ApiError } from '@/lib/api-client';
import { useTenantDial } from '@/hooks/use-tenant-dial';

const SUGGEST_DEBOUNCE_MS = 300;
const SUGGEST_MIN_DIGITS = 4;
const SUGGEST_LIMIT = 5;

/** E.164 phone: `+`, country code, national number. */
const PHONE_REGEX = /^\+[1-9]\d{6,14}$/;

type Mode = 'initial' | 'found' | 'not-found';

export interface CustomerCaptureModalProps {
  open: boolean;
  title: string;
  /** Cancel (Esc / backdrop / × close). Does NOT proceed to payment. */
  onClose: () => void;
  /** Existing or newly-created customer attached. Caller should open Payment. */
  onAttached: (customer: UICustomerLookupResult) => void;
  /**
   * Cashier confirms walk-in. Caller should mark skip and open Payment.
   * Omit to hide the Skip button when a customer is required (e.g. quotes).
   */
  onSkipped?: () => void;
}

function projectToUI(c: ApiCustomerLookupResult): UICustomerLookupResult {
  return {
    id: c.id,
    name: c.name,
    phone: c.phone,
    loyaltyPoints: c.loyalty_points,
    whatsapp_opt_out: c.whatsapp_opt_out,
  };
}

export function CustomerCaptureModal({
  open,
  title,
  onClose,
  onAttached,
  onSkipped,
}: CustomerCaptureModalProps) {
  const dial = useTenantDial();
  const [phone, _setPhone] = useState('');

  // Wrap setPhone so editing always returns the modal to 'initial' mode (any
  // earlier 'found' / 'not-found' result is for a stale phone value).
  const setPhone = useCallback((v: string) => {
    _setPhone(v);
    setMode((m) => (m === 'initial' ? m : 'initial'));
    setFound(null);
  }, []);
  const [mode, setMode] = useState<Mode>('initial');
  const [found, setFound] = useState<ApiCustomerLookupResult | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  // CU4-003: opted in by default (decision C9).
  const [marketing, setMarketing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<CustomerListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const phoneInputRef = useRef<HTMLInputElement | null>(null);

  // `phone` holds the full E.164 string (Input variant="phone" defaultDial={dial} emits it).
  const phoneLengthMessage = phoneLengthError(phone);
  const phoneValid = PHONE_REGEX.test(phone) && !phoneLengthMessage;

  // Reset whenever the modal closes; auto-focus phone on open.
  useEffect(() => {
    if (!open) {
      _setPhone('');
      setMode('initial');
      setFound(null);
      setName('');
      setEmail('');
      setMarketing(true);
      setError(null);
      setLoading(false);
      setSuggestions([]);
      setSearching(false);
    } else {
      const t = setTimeout(() => {
        phoneInputRef.current?.focus();
        phoneInputRef.current?.setSelectionRange(
          phoneInputRef.current.value.length,
          phoneInputRef.current.value.length,
        );
      }, 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Debounced live search — fires only while typing in the phone field
  // (mode === 'initial'). Cleared on close, on Look-Up result, or while the
  // entered value is too short to be meaningful.
  useEffect(() => {
    if (!open || mode !== 'initial') {
      setSuggestions([]);
      setSearching(false);
      return;
    }
    if (phone.length < SUGGEST_MIN_DIGITS) {
      setSuggestions([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await customersService.list({
          search: phone,
          limit: SUGGEST_LIMIT,
          // A deleted customer must not be offered for attaching (QA 7.1.20).
          active: true,
        });
        if (!cancelled) setSuggestions(res.data ?? []);
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, mode, phone]);

  const runLookup = useCallback(async () => {
    if (!phoneValid || loading) return;
    setLoading(true);
    setError(null);
    setFound(null);
    setSuggestions([]);
    try {
      const result = await customersService.lookupByPhone(phone);
      setFound(result);
      setMode('found');
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 404) {
        setMode('not-found');
      } else {
        setError(err instanceof ApiError ? err.message : 'Lookup failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [phone, phoneValid, loading]);

  const handleAttachExisting = useCallback(() => {
    if (!found) return;
    onAttached(projectToUI(found));
  }, [found, onAttached]);

  const handleChooseAnother = useCallback(() => {
    setFound(null);
    setMode('initial');
    setError(null);
    phoneInputRef.current?.focus();
  }, []);

  const handleAddNew = useCallback(async () => {
    if (!phoneValid || !name.trim() || loading) return;
    setLoading(true);
    setError(null);
    try {
      const created = await customersService.create({
        name: name.trim(),
        phone,
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(marketing ? {} : { marketing_opt_out: true }),
      });
      onAttached({
        id: created.id,
        name: created.name,
        phone: created.phone,
        loyaltyPoints: created.loyalty_points,
        whatsapp_opt_out: !marketing,
      });
    } catch (err) {
      // Race: another cashier added the same phone in the meantime → fall back
      // to a fresh lookup and attach whatever the server returns.
      if (err instanceof ApiError && err.statusCode === 409) {
        try {
          const existing = await customersService.lookupByPhone(phone);
          onAttached(projectToUI(existing));
          return;
        } catch {
          setError('A customer with this phone already exists. Please try again.');
        }
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not add customer.');
      }
    } finally {
      setLoading(false);
    }
  }, [phone, phoneValid, name, email, marketing, loading, onAttached]);

  const handlePhoneKey = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (mode === 'initial') void runLookup();
      }
    },
    [mode, runLookup],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      variant="default"
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          {onSkipped && (
            <Button variant="ghost" className="mr-auto" onClick={onSkipped} disabled={loading}>
              Skip — walk-in customer
            </Button>
          )}
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <Alert variant="danger" title="Lookup failed" description={error} />
        )}

        <Input
          ref={phoneInputRef}
          variant="phone"
          defaultDial={dial}
          label="Phone number"
          value={phone}
          onChange={setPhone}
          onKeyDown={handlePhoneKey}
          helperText={
            searching
              ? 'Searching…'
              : suggestions.length > 0
                ? `${suggestions.length} match${suggestions.length === 1 ? '' : 'es'} — pick one below, or press Enter to look up.`
                : 'Start typing the phone number. Press Enter to look up.'
          }
          error={
            phone.length > 0 && !phoneValid && !searching
              ? (phoneLengthMessage ?? 'Enter a valid phone number')
              : undefined
          }
          suffix={
            <Button
              size="sm"
              variant="primary"
              onClick={runLookup}
              disabled={!phoneValid || loading}
              loading={loading && mode === 'initial'}
            >
              Look Up
            </Button>
          }
        />

        {mode === 'initial' && suggestions.length > 0 && (
          <ul
            role="listbox"
            aria-label="Customer suggestions"
            className="-mt-2 max-h-64 overflow-y-auto rounded-xl border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800 divide-y divide-neutral-100 dark:divide-neutral-700 shadow-sm animate-fade-in"
          >
            {suggestions.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected="false"
                  onClick={() =>
                    onAttached({
                      id: c.id,
                      name: c.name,
                      phone: c.phone,
                      loyaltyPoints: c.loyalty_points,
                    })
                  }
                  className="w-full px-3 py-2 text-left transition-colors hover:bg-brand-50 dark:hover:bg-brand-900/20 focus:outline-none focus:bg-brand-50 dark:focus:bg-brand-900/20"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-neutral-900 dark:text-white">
                        {c.name}
                      </div>
                      <div className="truncate text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
                        {c.phone}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
                      {c.loyalty_points > 0 && (
                        <span className="tabular-nums">🪙 {c.loyalty_points}</span>
                      )}
                      <span className="tabular-nums">
                        {c.total_visits} visit{c.total_visits === 1 ? '' : 's'}
                      </span>
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}

        {mode === 'found' && found && (
          <div className="rounded-xl border border-success-200 bg-success-50 p-4 dark:border-success-700 dark:bg-success-900/20 animate-fade-in">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-base font-semibold text-neutral-900 dark:text-white truncate">
                    {found.name}
                  </span>
                  <Badge variant="success" size="sm">
                    Existing
                  </Badge>
                </div>
                <div className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">
                  {found.phone}
                  {found.email ? ` · ${found.email}` : ''}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-600 dark:text-neutral-400">
                  <span>
                    🪙 <span className="font-medium tabular-nums">{found.loyalty_points}</span> pts
                  </span>
                  <span>
                    Visits <span className="font-medium tabular-nums">{found.total_visits}</span>
                  </span>
                  {found.tags?.length > 0 && (
                    <span className="flex items-center gap-1">
                      {found.tags.slice(0, 3).map((t) => (
                        <Badge key={t} variant="info" size="sm">
                          {t}
                        </Badge>
                      ))}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="primary" size="md" onClick={handleAttachExisting}>
                Attach & Continue
              </Button>
              <Button variant="ghost" size="md" onClick={handleChooseAnother}>
                Choose another
              </Button>
            </div>
          </div>
        )}

        {mode === 'not-found' && (
          <div className="rounded-xl border border-brand-200 bg-brand-50 p-4 dark:border-brand-700 dark:bg-brand-900/20 animate-fade-in space-y-3">
            <div className="text-sm text-neutral-700 dark:text-neutral-200">
              No customer with this phone. Add a new one:
            </div>
            <Input
              type="text"
              label="Name"
              placeholder="Customer name"
              value={name}
              onChange={setName}
              required
            />
            <Input
              type="email"
              label="Email (optional)"
              placeholder="customer@example.com"
              value={email}
              onChange={setEmail}
            />
            <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 accent-brand-600"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
              />
              Send marketing messages
            </label>
            <div className="flex gap-2">
              <Button
                variant="primary"
                onClick={handleAddNew}
                disabled={!name.trim() || loading}
                loading={loading && mode === 'not-found'}
              >
                Add & Continue
              </Button>
              <Button variant="ghost" onClick={handleChooseAnother} disabled={loading}>
                Use a different number
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
